import { useState, type ReactNode } from 'react';
import { z } from 'zod';
import { T, Var, useGT } from 'gt-react';
import { PlusIcon } from '@phosphor-icons/react';
import {
  TaskOutcomeFieldsSchema,
  taskOutcomeConfigurationErrors,
  taskOutcomeFieldAppliesTo,
  taskOutcomeFieldsForOutcome,
  type TaskOutcomeField,
} from '@erp/shared-types';
import { useQuery } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import { useFeatureFlag } from 'src/integrations/feature-flags/feature-flags';
import { Button } from 'src/components/ui/button';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'src/components/ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from 'src/components/ui/select';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { useOutcomeEditorRows } from './useOutcomeEditorRows';
import { TaskTypeSection } from './TaskTypeEditorSections';
import { TaskOutcomeFieldRow } from './TaskOutcomeFieldRow';
import { useFieldTypeLabel } from './useFieldTypeLabel';
import {
  newFieldForOutcome,
  outcomeResultEntries,
  relabelField,
  setFieldRequired,
  toggleFieldOutcome,
} from './taskOutcomeResultModel';

const MAX_OUTCOME_FIELDS = 50;

function outcomeFieldConfigurationErrors(
  fields: TaskOutcomeField[],
  outcomes: string[],
): string[] {
  const parsed = TaskOutcomeFieldsSchema.safeParse(fields);
  return fields.length
    ? [
        ...(parsed.success
          ? []
          : parsed.error.issues.map((issue) =>
              [issue.path.join('.'), issue.message].filter(Boolean).join(': '),
            )),
        ...taskOutcomeConfigurationErrors(fields, outcomes),
      ]
    : [];
}

/**
 * Per-outcome result typing: for each option of the type's outcome enum, the
 * typed fields recorded when a task closes with it. One tab per outcome; a
 * field can be shared across outcomes and is stored once.
 */
export function TaskOutcomeFieldsEditor({
  fields,
  enumId,
  disabled,
  onChange,
}: {
  fields: TaskOutcomeField[];
  enumId: string;
  disabled: boolean;
  onChange: (fields: TaskOutcomeField[]) => void;
}) {
  const state = useOutcomeResultsEditor(fields, enumId, disabled, onChange);
  if (!state.visible) return null;
  const {
    editor,
    outcomes,
    active,
    errors,
    locked,
    lastAddedId,
    newRowIds,
    setSelected,
    replace,
    addField,
  } = state;

  return (
    <TaskTypeSection
      title={<T context="task type editor section title">Outcome results</T>}
      description={
        <T context="task type editor outcome results section description">
          For each way this task can end, define the typed data the closer — a
          person, an action or an agent — records with it. Agents get this as
          their result schema. Changes apply when work starts; started tasks
          keep their contract.
        </T>
      }
    >
      {state.ready ? (
        // Only the editing controls are disabled when locked: tab switching
        // and the summary stay usable so read-only fields can be inspected.
        <div className="flex min-w-0 flex-col gap-4">
          <Tabs value={active} onValueChange={setSelected}>
            <TabsList className="h-auto flex-wrap">
              {outcomes.map((outcome) => (
                <TabsTrigger key={outcome} value={outcome} className="gap-1.5">
                  <span className="font-mono">{outcome}</span>
                  <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                    {taskOutcomeFieldsForOutcome(fields, outcome).length}
                  </span>
                </TabsTrigger>
              ))}
            </TabsList>
            {outcomes.map((outcome) => (
              <TabsContent
                key={outcome}
                value={outcome}
                className="flex flex-col gap-2 pt-2"
              >
                <fieldset
                  disabled={locked}
                  className="flex min-w-0 flex-col gap-2"
                >
                  <OutcomeTabPanel
                    outcome={outcome}
                    outcomes={outcomes}
                    rows={editor.rows}
                    canAdd={!locked && fields.length < MAX_OUTCOME_FIELDS}
                    locked={locked}
                    lastAddedId={lastAddedId}
                    isNewRow={(rowId) => newRowIds.has(rowId)}
                    onAdd={() => addField(outcome)}
                    onReplace={replace}
                  />
                </fieldset>
              </TabsContent>
            ))}
          </Tabs>
          {errors.length > 0 && (
            <AlertContainer variant="destructive">
              <AlertDescription>{errors.join('; ')}</AlertDescription>
            </AlertContainer>
          )}
          <OutcomeResultContract
            fields={fields}
            outcomes={outcomes}
            active={active}
            onSelect={setSelected}
          />
        </div>
      ) : (
        <OutcomeResultsStatus hasEnum={state.hasEnum} isError={state.isError} />
      )}
    </TaskTypeSection>
  );
}

/** State, outcome options and edit operations behind the outcome results editor. */
function useOutcomeResultsEditor(
  fields: TaskOutcomeField[],
  enumId: string,
  disabled: boolean,
  onChange: (fields: TaskOutcomeField[]) => void,
) {
  const editor = useOutcomeEditorRows(fields, onChange);
  const [newRowIds, setNewRowIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const { enabled } = useFeatureFlag('task-outcome-fields-enabled', false);
  const hasEnum = z.uuid().safeParse(enumId).success;
  const optionsQuery = useQuery({
    queryKey: ['task-outcome-options', enumId],
    queryFn: () => rpcClient.taskTypes.getOutcomeOptions({ enumId }),
    enabled: hasEnum && (enabled || fields.length > 0),
    staleTime: 0,
  });

  function replace(rowId: string, next: TaskOutcomeField | null) {
    if (next) editor.update(rowId, next);
    else editor.remove(rowId);
  }
  function addField(outcome: string) {
    const rowId = editor.add(newFieldForOutcome(outcome));
    setNewRowIds(new Set([...newRowIds, rowId]));
    setLastAddedId(rowId);
  }

  return {
    // Read-only for existing fields when authoring is off; hidden otherwise.
    visible: enabled || fields.length > 0,
    locked: disabled || !enabled,
    hasEnum,
    isError: optionsQuery.isError,
    ...outcomeView(fields, optionsQuery, hasEnum, selected),
    editor,
    lastAddedId,
    newRowIds,
    setSelected,
    replace,
    addField,
  };
}

/** Outcome list, active tab and configuration errors derived from the options query. */
function outcomeView(
  fields: TaskOutcomeField[],
  query: { data?: string[]; isError: boolean; isSuccess: boolean },
  hasEnum: boolean,
  selected: string | null,
) {
  const outcomes = query.data ?? [];
  const keepSelected = selected !== null && outcomes.includes(selected);
  return {
    outcomes,
    ready: hasEnum && !query.isError && outcomes.length > 0,
    active: keepSelected ? selected : (outcomes[0] ?? ''),
    errors: query.isSuccess
      ? outcomeFieldConfigurationErrors(fields, outcomes)
      : [],
  };
}

/** Why the per-outcome editor cannot show yet: no enum, load error, or loading. */
function OutcomeResultsStatus({
  hasEnum,
  isError,
}: {
  hasEnum: boolean;
  isError: boolean;
}) {
  if (!hasEnum)
    return (
      <p className="text-sm text-muted-foreground">
        <T context="hint when no custom outcome enum is selected">
          Choose a custom outcome list under &quot;How can this task end?&quot;
          to give each outcome its own result fields.
        </T>
      </p>
    );
  if (isError)
    return (
      <p role="alert" className="text-sm text-destructive">
        <T context="outcome enum load error">
          Unable to load the outcome options. Refresh before editing
          requirements.
        </T>
      </p>
    );
  return (
    <p className="text-sm text-muted-foreground">
      <T context="outcome options loading or empty">Loading outcome options…</T>
    </p>
  );
}

/** One outcome's tab: its field rows wired to the pure edits in the model. */
function OutcomeTabPanel({
  outcome,
  outcomes,
  rows,
  canAdd,
  locked,
  lastAddedId,
  isNewRow,
  onAdd,
  onReplace,
}: {
  outcome: string;
  outcomes: string[];
  rows: EditorRow[];
  canAdd: boolean;
  locked: boolean;
  lastAddedId: string | null;
  isNewRow: (rowId: string) => boolean;
  onAdd: () => void;
  onReplace: (rowId: string, next: TaskOutcomeField | null) => void;
}) {
  return (
    <OutcomeFieldList
      outcome={outcome}
      outcomes={outcomes}
      rows={rows}
      canAdd={canAdd}
      onAdd={onAdd}
      onReuse={(rowId, field) =>
        onReplace(rowId, toggleFieldOutcome(field, outcome, true, outcomes))
      }
      renderRow={(rowId, field) => (
        <TaskOutcomeFieldRow
          key={rowId}
          field={field}
          outcome={outcome}
          vocabulary={outcomes}
          autoFocus={rowId === lastAddedId}
          readOnly={locked}
          onLabelChange={(label) =>
            onReplace(rowId, relabelField(field, label, isNewRow(rowId)))
          }
          onChange={(patch) => onReplace(rowId, { ...field, ...patch })}
          onRequiredChange={(required) =>
            onReplace(rowId, setFieldRequired(field, outcome, required))
          }
          onOutcomeToggle={(entry, included) =>
            onReplace(
              rowId,
              toggleFieldOutcome(field, entry, included, outcomes),
            )
          }
          onRemove={() =>
            onReplace(
              rowId,
              toggleFieldOutcome(field, outcome, false, outcomes),
            )
          }
        />
      )}
    />
  );
}

type EditorRow = { id: string; value: TaskOutcomeField };

function OutcomeFieldList({
  outcome,
  outcomes,
  rows,
  canAdd,
  onAdd,
  onReuse,
  renderRow,
}: {
  outcome: string;
  outcomes: string[];
  rows: EditorRow[];
  canAdd: boolean;
  onAdd: () => void;
  onReuse: (rowId: string, field: TaskOutcomeField) => void;
  renderRow: (rowId: string, field: TaskOutcomeField) => ReactNode;
}) {
  const gt = useGT();
  const own = rows.filter((row) =>
    taskOutcomeFieldAppliesTo(row.value, outcome),
  );
  const reusable = rows.filter(
    (row) =>
      !taskOutcomeFieldAppliesTo(row.value, outcome) && row.value.key !== '',
  );
  return (
    <>
      {own.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          <T context="empty state for an outcome with no result fields">
            Closing as <Var>{outcome}</Var> records only the outcome and a
            reason. Add fields to capture typed data with it.
          </T>
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {own.map((row) => renderRow(row.id, row.value))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canAdd}
          onClick={onAdd}
        >
          <PlusIcon />
          <T context="add a result field to one task outcome">Add Field</T>
        </Button>
        {reusable.length > 0 && (
          <Select
            value=""
            disabled={!canAdd}
            onValueChange={(rowId) => {
              const row = reusable.find((entry) => entry.id === rowId);
              if (row) onReuse(row.id, row.value);
            }}
          >
            <SelectTrigger
              className="h-8 w-auto"
              aria-label={gt('Reuse a field from another outcome', {
                context: 'picker to share an existing result field',
              })}
            >
              <SelectValue
                placeholder={gt('Reuse field from another outcome…', {
                  context: 'placeholder to share an existing result field',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {reusable.map((row) => (
                  <SelectItem key={row.id} value={row.id}>
                    {row.value.label || row.value.key}{' '}
                    <span className="font-mono text-xs text-muted-foreground">
                      {outcomes
                        .filter((entry) =>
                          taskOutcomeFieldAppliesTo(row.value, entry),
                        )
                        .join(', ')}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      </div>
    </>
  );
}

/** Compact typed signature per outcome, e.g. approved → amount: Number*. */
function OutcomeResultContract({
  fields,
  outcomes,
  active,
  onSelect,
}: {
  fields: TaskOutcomeField[];
  outcomes: string[];
  active: string;
  onSelect: (outcome: string) => void;
}) {
  const typeLabel = useFieldTypeLabel();
  return (
    <div className="flex flex-col gap-1.5 rounded-md bg-muted/50 p-3">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <T context="summary of typed result per task outcome">
          Result contract
        </T>
      </span>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        {outcomes.map((outcome) => {
          const entries = outcomeResultEntries(fields, outcome);
          return (
            <div key={outcome} className="contents">
              <dt>
                <button
                  type="button"
                  className={`font-mono hover:underline ${outcome === active ? 'font-semibold' : 'text-muted-foreground'}`}
                  onClick={() => onSelect(outcome)}
                >
                  {outcome}
                </button>
              </dt>
              <dd className="flex flex-wrap gap-1">
                {entries.length === 0 ? (
                  <span className="text-muted-foreground">
                    <T context="outcome without result fields">
                      outcome + reason only
                    </T>
                  </span>
                ) : (
                  entries.map((entry) => (
                    <code
                      key={`${entry.key}-${entry.label}`}
                      title={entry.label}
                      className="rounded bg-background px-1.5 py-0.5 text-xs"
                    >
                      {entry.key || '…'}: {typeLabel(entry.type)}
                      {entry.required ? '*' : '?'}
                    </code>
                  ))
                )}
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}
